import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import {
    ArrowLeft, Plus, FileText, Receipt, Trash, Eye, Download, Buildings, Warning, CheckCircle, ShieldCheck, Upload,
} from "@phosphor-icons/react";

const EUR = (n) => (typeof n === "number" ? n.toLocaleString("es-ES", { style: "currency", currency: "EUR" }) : "—");

const REGIMENES = [
    { v: "01", t: "01 · Régimen general" },
    { v: "02", t: "02 · Exportación" },
    { v: "05", t: "05 · Agencias de viaje" },
    { v: "07", t: "07 · Criterio de caja" },
];
const TIPOS = [
    { v: "F1", t: "F1 · Completa" },
    { v: "F2", t: "F2 · Simplificada" },
    { v: "R1", t: "R1 · Rectificativa" },
];
const IVA_OPTS = [0, 4, 10, 21];

function nuevaLinea() {
    return { descripcion: "", cantidad: 1, precio_unit: 0, tipo_iva: 21, descuento: 0 };
}

export default function Verifactu() {
    const [empresas, setEmpresas] = useState([]);
    const [empresaId, setEmpresaId] = useState("");
    const [emisor, setEmisor] = useState(null);
    const [certInfo, setCertInfo] = useState(null);
    const [certUploading, setCertUploading] = useState(false);
    const [facturas, setFacturas] = useState([]);
    const [showEditor, setShowEditor] = useState(false);
    const [showEmisor, setShowEmisor] = useState(false);
    const [loading, setLoading] = useState(false);

    // Editor state
    const today = new Date().toISOString().slice(0, 10);
    const [ed, setEd] = useState({
        tipo: "F1", serie: "", fecha_expedicion: today, fecha_operacion: today, regimen: "01",
        receptor_nombre: "", receptor_nif: "", receptor_direccion: "",
        lineas: [nuevaLinea()], retencion_pct: 0, suplidos: 0, notas: "",
    });

    async function loadEmpresas() {
        try {
            const { data } = await api.get("/verifactu/empresas");
            setEmpresas(data.items || []);
            if (!empresaId && data.items?.length) setEmpresaId(data.items[0].id);
        } catch (e) { /* ignore */ }
    }
    async function loadEmisor(id) {
        if (!id) return;
        try {
            const { data } = await api.get(`/verifactu/emisores/${id}`);
            setEmisor(data);
        } catch (e) { setEmisor(null); }
        try {
            const { data } = await api.get(`/verifactu/emisores/${id}/certificado`);
            setCertInfo(data);
        } catch (e) { setCertInfo(null); }
    }
    async function loadFacturas(id) {
        if (!id) return;
        try {
            const { data } = await api.get(`/verifactu/facturas?empresa_id=${id}`);
            setFacturas(data.items || []);
        } catch (e) { setFacturas([]); }
    }

    useEffect(() => { loadEmpresas(); }, []); // eslint-disable-line
    useEffect(() => { if (empresaId) { loadEmisor(empresaId); loadFacturas(empresaId); } }, [empresaId]);

    const totales = useMemo(() => {
        const bases = {};
        for (const l of ed.lineas) {
            const bruto = (Number(l.cantidad) || 0) * (Number(l.precio_unit) || 0);
            const neto = bruto * (1 - (Number(l.descuento) || 0) / 100);
            const t = Number(l.tipo_iva) || 0;
            bases[t] = (bases[t] || 0) + neto;
        }
        const base = Object.values(bases).reduce((a, b) => a + b, 0);
        const iva = Object.entries(bases).reduce((a, [t, b]) => a + b * Number(t) / 100, 0);
        const ret = base * (Number(ed.retencion_pct) || 0) / 100;
        const total = base + iva - ret + (Number(ed.suplidos) || 0);
        return { base, iva, ret, total, bases };
    }, [ed.lineas, ed.retencion_pct, ed.suplidos]);

    async function saveFactura() {
        if (!empresaId) return alert("Selecciona una empresa emisora.");
        if (!ed.receptor_nombre || !ed.receptor_nif) return alert("El receptor debe tener nombre y NIF.");
        if (!ed.lineas?.length || !ed.lineas.some(l => l.descripcion && (l.precio_unit || 0) > 0)) return alert("Añade al menos una línea con importe.");
        setLoading(true);
        try {
            const payload = {
                empresa_id: empresaId,
                tipo: ed.tipo, serie: ed.serie || null,
                fecha_expedicion: ed.fecha_expedicion,
                fecha_operacion: ed.fecha_operacion || ed.fecha_expedicion,
                regimen: ed.regimen,
                receptor_nombre: ed.receptor_nombre,
                receptor_nif: ed.receptor_nif.toUpperCase().replace(/[\s.-]/g, ""),
                receptor_direccion: ed.receptor_direccion,
                lineas: ed.lineas.filter(l => l.descripcion),
                retencion_pct: Number(ed.retencion_pct) || 0,
                suplidos: Number(ed.suplidos) || 0,
                notas: ed.notas || "",
            };
            const { data } = await api.post("/verifactu/facturas", payload);
            setShowEditor(false);
            setEd({
                tipo: "F1", serie: "", fecha_expedicion: today, fecha_operacion: today, regimen: "01",
                receptor_nombre: "", receptor_nif: "", receptor_direccion: "",
                lineas: [nuevaLinea()], retencion_pct: 0, suplidos: 0, notas: "",
            });
            await loadFacturas(empresaId);
            alert(`Factura ${data.numero_completo} emitida. Total ${EUR(data.total)}.`);
        } catch (e) {
            alert(formatApiError(e, "Error al emitir la factura"));
        } finally { setLoading(false); }
    }

    async function downloadPdf(f) {
        try {
            const token = localStorage.getItem("gf347_token");
            const r = await fetch(`${API}/verifactu/facturas/${f.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `${f.numero_completo}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch { alert("No se pudo descargar el PDF."); }
    }

    async function anular(f) {
        if (!confirm(`¿Anular la factura ${f.numero_completo}? Verifactu no permite borrarla, se marcará como ANULADA para preservar la cadena.`)) return;
        try {
            await api.delete(`/verifactu/facturas/${f.id}`);
            await loadFacturas(empresaId);
        } catch (e) { alert(formatApiError(e, "Error al anular")); }
    }

    async function saveEmisor(payload) {
        try {
            const { data } = await api.put(`/verifactu/emisores/${empresaId}`, payload);
            setEmisor(data);
            setShowEmisor(false);
            alert("Datos del emisor actualizados.");
        } catch (e) { alert(formatApiError(e, "Error al guardar")); }
    }

    async function uploadCertificado(file, passphrase) {
        if (!file) return alert("Selecciona un fichero .p12/.pfx");
        setCertUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("passphrase", passphrase || "");
            const token = localStorage.getItem("gf347_token");
            const r = await fetch(`${API}/verifactu/emisores/${empresaId}/certificado`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
            await loadEmisor(empresaId);
            alert(`Certificado guardado.\nTitular: ${data.meta.cn}\nEmisor: ${data.meta.emisor}\nVálido hasta: ${data.meta.not_after?.slice(0, 10)}`);
        } catch (e) {
            alert("Error al subir el certificado:\n" + (e.message || e));
        } finally { setCertUploading(false); }
    }

    async function deleteCertificado() {
        if (!confirm("¿Eliminar el certificado digital de esta empresa? No podrás firmar facturas hasta que subas otro.")) return;
        try {
            await api.delete(`/verifactu/emisores/${empresaId}/certificado`);
            await loadEmisor(empresaId);
        } catch (e) { alert(formatApiError(e, "Error al eliminar")); }
    }

    async function verificarCertificado() {
        try {
            const { data } = await api.post(`/verifactu/emisores/${empresaId}/certificado/verificar`);
            alert(`✅ Certificado OK\nTitular: ${data.meta.cn}\nVálido hasta: ${data.meta.not_after?.slice(0, 10)}`);
        } catch (e) { alert(formatApiError(e, "El certificado no se puede verificar")); }
    }

    async function recalcularCadena() {
        if (!confirm("Recalcular QR y huella de todas las facturas emitidas de esta empresa con el formato oficial de AEAT (fechas DD-MM-YYYY). ¿Continuar?")) return;
        try {
            const { data } = await api.post(`/verifactu/facturas/recalcular-cadena?empresa_id=${empresaId}`);
            await loadFacturas(empresaId);
            alert(`✅ ${data.updated} facturas actualizadas con el formato correcto.`);
        } catch (e) { alert(formatApiError(e, "Error al recalcular")); }
    }

    return (
        <div className="min-h-screen bg-[var(--gf-cream)]">
            <Navbar />
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
                <Link to="/" className="text-xs text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)] inline-flex items-center gap-1 mb-3">
                    <ArrowLeft size={12} /> Volver
                </Link>

                <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                    <div>
                        <h1 className="serif text-3xl md:text-4xl text-[var(--gf-navy)] inline-flex items-center gap-3">
                            <Receipt size={28} weight="light" /> Facturación Verifactu
                        </h1>
                        <p className="text-sm text-[var(--gf-text-secondary)] mt-1">
                            Emite facturas conforme al RD 1007/2023 · Numeración correlativa · Huella encadenada · QR de comprobación AEAT
                        </p>
                    </div>
                    <button className="btn-gold inline-flex items-center gap-2" onClick={() => setShowEditor(true)} disabled={!empresaId} data-testid="btn-new-factura">
                        <Plus size={14} /> Nueva factura
                    </button>
                </div>

                {/* Selector de empresa emisora */}
                <div className="gf-card mb-6">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <Buildings size={18} className="text-[var(--gf-navy)]" />
                            <label className="text-sm font-semibold text-[var(--gf-navy)]">Empresa emisora:</label>
                            <select value={empresaId} onChange={(e) => setEmpresaId(e.target.value)} className="gf-input" data-testid="select-empresa">
                                <option value="">— Selecciona —</option>
                                {empresas.map((e) => <option key={e.id} value={e.id}>{e.nombre} · {e.cif}</option>)}
                            </select>
                        </div>
                        {emisor && (
                            <button className="btn-outline text-xs" onClick={() => setShowEmisor(true)} data-testid="btn-edit-emisor">
                                Editar datos fiscales
                            </button>
                        )}
                    </div>
                    {emisor && (
                        <div className="mt-3 text-xs text-[var(--gf-text-secondary)] flex items-center gap-4 flex-wrap">
                            <div>
                                <b>{emisor.razon_social}</b> · NIF <span className="mono">{emisor.nif}</span>
                                {emisor.direccion && <> · {emisor.direccion}</>}
                                · Serie por defecto: <span className="mono">{emisor.serie_default || "A"}</span>
                            </div>
                            {certInfo?.has_cert ? (
                                <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[11px] ${(certInfo.dias_para_expirar || 0) < 30 ? "bg-amber-100 border-amber-400 text-amber-800" : "bg-emerald-50 border-emerald-300 text-emerald-800"}`}
                                    title={`Titular: ${certInfo.meta?.cn} · Vál. hasta ${certInfo.meta?.not_after?.slice(0, 10)}`}
                                    data-testid="badge-cert-ok"
                                >
                                    <ShieldCheck size={12} weight="fill" /> Certificado activo · {certInfo.meta?.cn?.slice(0, 30)}
                                    {certInfo.dias_para_expirar != null && <span className="ml-1 opacity-70">· caduca en {certInfo.dias_para_expirar} d</span>}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-50 border border-rose-300 text-rose-700 text-[11px]" data-testid="badge-cert-missing">
                                    <Warning size={12} weight="fill" /> Sin certificado digital
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Listado de facturas */}
                <div className="gf-card">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="serif text-lg text-[var(--gf-navy)]">Facturas emitidas · {facturas.length}</h3>
                        {facturas.length > 0 && (
                            <button onClick={recalcularCadena} className="btn-outline text-xs" title="Recalcula el QR y la huella de todas las facturas con el formato oficial AEAT (DD-MM-YYYY)" data-testid="btn-recalcular-cadena">
                                Recalcular QR/Huella
                            </button>
                        )}
                    </div>
                    {facturas.length === 0 ? (
                        <p className="text-sm text-[var(--gf-text-muted)] italic py-6 text-center">Aún no has emitido ninguna factura. Pulsa "Nueva factura" para empezar.</p>
                    ) : (
                        <div className="overflow-auto">
                            <table className="w-full text-sm gf-table">
                                <thead>
                                    <tr>
                                        <th>Nº</th>
                                        <th>Tipo</th>
                                        <th>Fecha</th>
                                        <th>Cliente</th>
                                        <th>NIF</th>
                                        <th className="text-right">Base</th>
                                        <th className="text-right">IVA</th>
                                        <th className="text-right">Total</th>
                                        <th>Estado</th>
                                        <th className="text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {facturas.map((f) => (
                                        <tr key={f.id} data-testid={`row-factura-${f.id}`}>
                                            <td className="mono text-xs font-semibold">{f.numero_completo}</td>
                                            <td><span className="text-[10px] uppercase tracking-widest bg-[var(--gf-navy)]/10 px-2 py-0.5">{f.tipo}</span></td>
                                            <td className="mono text-xs">{f.fecha_expedicion}</td>
                                            <td className="max-w-[220px] truncate" title={f.receptor_nombre}>{f.receptor_nombre}</td>
                                            <td className="mono text-xs">{f.receptor_nif}</td>
                                            <td className="text-right mono text-xs">{EUR(f.base_imponible)}</td>
                                            <td className="text-right mono text-xs">{EUR(f.cuota_iva)}</td>
                                            <td className="text-right mono text-xs font-semibold">{EUR(f.total)}</td>
                                            <td>
                                                {f.estado === "anulada" ? (
                                                    <span className="text-[10px] uppercase bg-rose-100 text-rose-700 px-2 py-0.5">Anulada</span>
                                                ) : (
                                                    <span className="text-[10px] uppercase bg-emerald-100 text-emerald-700 px-2 py-0.5">Emitida</span>
                                                )}
                                            </td>
                                            <td className="text-right whitespace-nowrap">
                                                <button onClick={() => downloadPdf(f)} className="p-1.5 text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)]" title="Descargar PDF" data-testid={`btn-pdf-${f.id}`}>
                                                    <Download size={14} />
                                                </button>
                                                {f.estado !== "anulada" && (
                                                    <button onClick={() => anular(f)} className="p-1.5 text-[var(--gf-danger)] hover:opacity-70" title="Anular" data-testid={`btn-anular-${f.id}`}>
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Aviso Fase 1 */}
                <div className="gf-card mt-6 border-l-4 border-l-amber-500 bg-amber-50">
                    <div className="text-xs text-amber-900 inline-flex items-start gap-2">
                        <Warning size={14} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                        <div>
                            <b>Fase 1 · Modo offline (Verifactu preparado, sin envío a AEAT).</b> Las facturas quedan almacenadas con numeración correlativa, huella encadenada y QR con URL de comprobación. En Fase 2 se activará el envío real firmando con el certificado digital de cada empresa (sandbox / producción).
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal editor de factura */}
            {showEditor && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-auto" onClick={() => !loading && setShowEditor(false)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); saveFactura(); }} className="bg-white p-6 md:p-8 max-w-4xl w-full space-y-4 my-8" data-testid="modal-editor-factura">
                        <div className="flex items-center justify-between">
                            <h2 className="serif text-2xl text-[var(--gf-navy)]">Nueva factura</h2>
                            <button type="button" onClick={() => setShowEditor(false)} className="text-2xl text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)]">×</button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div><label className="gf-label">Tipo</label>
                                <select value={ed.tipo} onChange={(e) => setEd({ ...ed, tipo: e.target.value })} className="gf-input">
                                    {TIPOS.map(t => <option key={t.v} value={t.v}>{t.t}</option>)}
                                </select>
                            </div>
                            <div><label className="gf-label">Serie</label>
                                <input value={ed.serie} onChange={(e) => setEd({ ...ed, serie: e.target.value.toUpperCase() })} placeholder={emisor?.serie_default || "A"} className="gf-input mono uppercase" maxLength={4} />
                            </div>
                            <div><label className="gf-label">Fecha expedición</label>
                                <input type="date" value={ed.fecha_expedicion} onChange={(e) => setEd({ ...ed, fecha_expedicion: e.target.value })} className="gf-input mono" required />
                            </div>
                            <div><label className="gf-label">Fecha operación</label>
                                <input type="date" value={ed.fecha_operacion} onChange={(e) => setEd({ ...ed, fecha_operacion: e.target.value })} className="gf-input mono" />
                            </div>
                            <div className="col-span-2 md:col-span-4"><label className="gf-label">Régimen IVA</label>
                                <select value={ed.regimen} onChange={(e) => setEd({ ...ed, regimen: e.target.value })} className="gf-input">
                                    {REGIMENES.map(r => <option key={r.v} value={r.v}>{r.t}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="border-t border-[var(--gf-border)] pt-3">
                            <h3 className="text-sm font-semibold text-[var(--gf-navy)] mb-2">Cliente / Receptor</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="md:col-span-2"><label className="gf-label">Razón social</label>
                                    <input value={ed.receptor_nombre} onChange={(e) => setEd({ ...ed, receptor_nombre: e.target.value })} className="gf-input" required />
                                </div>
                                <div><label className="gf-label">NIF / CIF</label>
                                    <input value={ed.receptor_nif} onChange={(e) => setEd({ ...ed, receptor_nif: e.target.value })} className="gf-input mono uppercase" required />
                                </div>
                                <div className="md:col-span-3"><label className="gf-label">Dirección</label>
                                    <input value={ed.receptor_direccion} onChange={(e) => setEd({ ...ed, receptor_direccion: e.target.value })} className="gf-input" />
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-[var(--gf-border)] pt-3">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-semibold text-[var(--gf-navy)]">Líneas</h3>
                                <button type="button" className="btn-outline text-xs" onClick={() => setEd({ ...ed, lineas: [...ed.lineas, nuevaLinea()] })} data-testid="btn-add-linea">
                                    <Plus size={12} /> Añadir línea
                                </button>
                            </div>
                            <div className="overflow-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-[10px] uppercase tracking-widest text-[var(--gf-text-muted)]">
                                            <th className="text-left pb-1">Descripción</th>
                                            <th className="text-right pb-1 w-16">Cant.</th>
                                            <th className="text-right pb-1 w-24">P. Unit</th>
                                            <th className="text-right pb-1 w-16">IVA</th>
                                            <th className="text-right pb-1 w-16">Dto %</th>
                                            <th className="text-right pb-1 w-24">Total</th>
                                            <th className="pb-1 w-8"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ed.lineas.map((l, i) => {
                                            const bruto = (Number(l.cantidad) || 0) * (Number(l.precio_unit) || 0);
                                            const neto = bruto * (1 - (Number(l.descuento) || 0) / 100);
                                            return (
                                                <tr key={i}>
                                                    <td><input value={l.descripcion} onChange={(e) => { const ls = [...ed.lineas]; ls[i].descripcion = e.target.value; setEd({ ...ed, lineas: ls }); }} className="gf-input" placeholder="Concepto…" /></td>
                                                    <td><input type="number" step="0.01" value={l.cantidad} onChange={(e) => { const ls = [...ed.lineas]; ls[i].cantidad = e.target.value; setEd({ ...ed, lineas: ls }); }} className="gf-input text-right mono" /></td>
                                                    <td><input type="number" step="0.01" value={l.precio_unit} onChange={(e) => { const ls = [...ed.lineas]; ls[i].precio_unit = e.target.value; setEd({ ...ed, lineas: ls }); }} className="gf-input text-right mono" /></td>
                                                    <td><select value={l.tipo_iva} onChange={(e) => { const ls = [...ed.lineas]; ls[i].tipo_iva = Number(e.target.value); setEd({ ...ed, lineas: ls }); }} className="gf-input text-right">
                                                        {IVA_OPTS.map(v => <option key={v} value={v}>{v}%</option>)}
                                                    </select></td>
                                                    <td><input type="number" step="0.1" min="0" max="100" value={l.descuento} onChange={(e) => { const ls = [...ed.lineas]; ls[i].descuento = e.target.value; setEd({ ...ed, lineas: ls }); }} className="gf-input text-right mono" /></td>
                                                    <td className="text-right mono">{EUR(neto)}</td>
                                                    <td>
                                                        {ed.lineas.length > 1 && (
                                                            <button type="button" onClick={() => setEd({ ...ed, lineas: ed.lineas.filter((_, k) => k !== i) })} className="text-[var(--gf-danger)] hover:opacity-70 p-1">
                                                                <Trash size={12} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div><label className="gf-label">Retención IRPF (%)</label>
                                <input type="number" step="0.1" min="0" max="100" value={ed.retencion_pct} onChange={(e) => setEd({ ...ed, retencion_pct: e.target.value })} className="gf-input text-right mono" />
                            </div>
                            <div><label className="gf-label">Suplidos (€ · IVA 0)</label>
                                <input type="number" step="0.01" min="0" value={ed.suplidos} onChange={(e) => setEd({ ...ed, suplidos: e.target.value })} className="gf-input text-right mono" />
                            </div>
                        </div>

                        <div className="bg-[var(--gf-cream)] border border-[var(--gf-border)] p-3">
                            <div className="flex justify-between text-sm"><span>Base imponible</span><span className="mono">{EUR(totales.base)}</span></div>
                            <div className="flex justify-between text-sm"><span>IVA</span><span className="mono">{EUR(totales.iva)}</span></div>
                            {totales.ret > 0 && <div className="flex justify-between text-sm text-rose-700"><span>Retención IRPF</span><span className="mono">-{EUR(totales.ret)}</span></div>}
                            {Number(ed.suplidos) > 0 && <div className="flex justify-between text-sm text-amber-700"><span>Suplidos</span><span className="mono">{EUR(Number(ed.suplidos))}</span></div>}
                            <div className="flex justify-between text-base font-bold text-[var(--gf-navy)] border-t border-[var(--gf-border)] pt-2 mt-2"><span>TOTAL</span><span className="mono">{EUR(totales.total)}</span></div>
                        </div>

                        <div><label className="gf-label">Notas / condiciones</label>
                            <textarea value={ed.notas} onChange={(e) => setEd({ ...ed, notas: e.target.value })} rows={2} className="gf-input" placeholder="Forma de pago, IBAN…" />
                        </div>

                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setShowEditor(false)} className="btn-outline text-sm" disabled={loading}>Cancelar</button>
                            <button type="submit" className="btn-gold text-sm inline-flex items-center gap-2" disabled={loading} data-testid="btn-save-factura">
                                {loading ? "Emitiendo…" : <><CheckCircle size={14} weight="bold" /> Emitir factura</>}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Modal editor de emisor */}
            {showEmisor && emisor && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-auto" onClick={() => setShowEmisor(false)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.target); saveEmisor({
                        razon_social: fd.get("razon_social"),
                        nif: fd.get("nif"),
                        direccion: fd.get("direccion"),
                        telefono: fd.get("telefono"),
                        email: fd.get("email"),
                        serie_default: (fd.get("serie_default") || "A").toString().toUpperCase(),
                    }); }} className="bg-white p-6 md:p-8 max-w-2xl w-full space-y-3 my-8" data-testid="modal-emisor">
                        <h2 className="serif text-xl text-[var(--gf-navy)]">Datos fiscales del emisor</h2>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2"><label className="gf-label">Razón social</label>
                                <input name="razon_social" defaultValue={emisor.razon_social} className="gf-input" required />
                            </div>
                            <div><label className="gf-label">NIF/CIF</label>
                                <input name="nif" defaultValue={emisor.nif} className="gf-input mono uppercase" required />
                            </div>
                            <div><label className="gf-label">Serie por defecto</label>
                                <input name="serie_default" defaultValue={emisor.serie_default || "A"} className="gf-input mono uppercase" maxLength={4} />
                            </div>
                            <div className="col-span-2"><label className="gf-label">Dirección</label>
                                <input name="direccion" defaultValue={emisor.direccion || ""} className="gf-input" />
                            </div>
                            <div><label className="gf-label">Teléfono</label>
                                <input name="telefono" defaultValue={emisor.telefono || ""} className="gf-input mono" />
                            </div>
                            <div><label className="gf-label">Email</label>
                                <input name="email" defaultValue={emisor.email || ""} className="gf-input" />
                            </div>
                        </div>

                        {/* Sección Certificado Digital */}
                        <div className="border-t border-[var(--gf-border)] pt-4 mt-4">
                            <h3 className="text-sm font-semibold text-[var(--gf-navy)] inline-flex items-center gap-2 mb-2">
                                <ShieldCheck size={16} /> Certificado digital (Verifactu)
                            </h3>
                            {certInfo?.has_cert ? (
                                <div className="bg-emerald-50 border border-emerald-300 p-3 text-xs space-y-1">
                                    <div><b>Titular:</b> {certInfo.meta?.cn}</div>
                                    {certInfo.meta?.organizacion && <div><b>Organización:</b> {certInfo.meta.organizacion}</div>}
                                    {certInfo.meta?.serial_number_titular && <div><b>NIF titular:</b> <span className="mono">{certInfo.meta.serial_number_titular}</span></div>}
                                    <div><b>Emisor:</b> {certInfo.meta?.emisor}</div>
                                    <div><b>Válido:</b> {certInfo.meta?.not_before?.slice(0, 10)} → {certInfo.meta?.not_after?.slice(0, 10)}
                                        {certInfo.dias_para_expirar != null && <span className={`ml-2 font-semibold ${certInfo.dias_para_expirar < 30 ? "text-amber-800" : "text-emerald-800"}`}>({certInfo.dias_para_expirar} días)</span>}
                                    </div>
                                    <div><b>Fichero:</b> <span className="mono">{certInfo.filename}</span> · {(certInfo.size / 1024).toFixed(1)} KB</div>
                                    <div className="flex gap-2 pt-2">
                                        <button type="button" onClick={verificarCertificado} className="btn-outline text-xs">Verificar</button>
                                        <button type="button" onClick={deleteCertificado} className="btn-outline text-xs text-rose-700 border-rose-300">Eliminar</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-amber-50 border border-amber-300 p-3 text-xs mb-2">
                                    Sube el <b>.p12/.pfx</b> de la empresa para poder firmar y enviar facturas a la AEAT (Fase 2). Se guarda cifrado con AES-256-GCM y sólo se descifra al firmar.
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                <div>
                                    <label className="gf-label">Fichero .p12 / .pfx</label>
                                    <input id="cert-file" type="file" accept=".p12,.pfx" className="gf-input !py-1 text-xs" data-testid="cert-file-input" />
                                </div>
                                <div>
                                    <label className="gf-label">Contraseña del certificado</label>
                                    <input id="cert-pass" type="password" className="gf-input mono text-xs" placeholder="•••••••" data-testid="cert-pass-input" />
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn-outline text-xs inline-flex items-center gap-1 mt-2"
                                onClick={() => {
                                    const f = document.getElementById("cert-file").files[0];
                                    const p = document.getElementById("cert-pass").value;
                                    uploadCertificado(f, p);
                                }}
                                disabled={certUploading}
                                data-testid="btn-upload-cert"
                            >
                                <Upload size={12} /> {certUploading ? "Subiendo…" : (certInfo?.has_cert ? "Reemplazar certificado" : "Subir certificado")}
                            </button>
                            <p className="text-[10px] text-[var(--gf-text-muted)] mt-2">La firma XAdES-BES y el envío SOAP a AEAT se activarán en Fase 2b una vez validados los certificados de todas las empresas.</p>
                        </div>

                        <div className="flex justify-end gap-2 pt-3">
                            <button type="button" onClick={() => setShowEmisor(false)} className="btn-outline text-sm">Cancelar</button>
                            <button type="submit" className="btn-gold text-sm">Guardar datos fiscales</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
